import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import styled from "styled-components";

import { couponApi, merchantApi } from "@api";
import { ComponentCouponList, CouponEmpty, IConClose, ModuleEmpty } from "@components";
import { enumNavigation, enumStatus, enumTypes } from "@configs";
import { ICoupon, ICouponList, IPagination, IRewardUsed } from "@interfaces";
import {
    fetchUser,
    selectApp,
    selectAuth,
    selectConfigClient,
    selectMerchant,
    setCouponCollected,
    setExternalTypes,
    setLoading,
} from "@redux";
import UIStore from "@Rocket-Innovation/components/store/useUIStore";
import { RenderMeta, formatMessage, useNotify } from "@utils";
import { Skeleton } from "antd";
import dayjs from "dayjs";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";
import CountdownTimer from "src/Components/shared/CountDownTimer";
import { Pending } from "src/Components/shared/Pending";
import PopupWarning from "src/Components/shared/PopupWarning";
import { Redeemed } from "src/Components/shared/Redeemed";
import { Used } from "src/Components/shared/Used";
import { formatDateStartEnd } from "src/Utils/dateUtil";
import { deduplicate } from "src/Utils/requestDeduper";

interface IProps {
    under: enumNavigation;
    usedData?: IRewardUsed | undefined;
    isConfirm?: boolean;
}

interface IDoneProps {
    data: any;
    handleConfirm?: () => void;
    handleCalculatePoint?: () => void;
    isConfirm?: boolean;
    isHistory?: boolean;
    isMerchantFreePoint?: boolean;
    quantity?: number;
    usedData?: IRewardUsed | undefined;
    cNote?: any;
}

export const ModulesCoupon = (props: IProps) => {
    //page hooks
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { themes } = useSelector(selectMerchant);
    const merchant = useSelector(selectMerchant);
    const { error, success, warning } = useNotify();
    //page state
    const { auth, userInfo } = useSelector(selectAuth);
    const [coupons, setCoupons] = useState<ICouponList[] | ICoupon[]>([]);
    const [loadingPagination, setLoadingPagination] = useState<boolean>(false);
    const [param, setParam] = useState<IPagination>({
        page: 1,
        limit: 100,
    });
    const [spinning, setSpinning] = useState<boolean>(false);
    //page props
    const { under, usedData, isConfirm = true } = props;
    const configClient = useSelector(selectConfigClient);
    const [cNote, setCNote] = useState<string>("");
    const [aCode, setACode] = useState<string>("");
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [focusCoupon, setFocusCoupon] = useState<any>();
    const [focusParentId, setFocusParentId] = useState<string>("");
    const { langSetting } = useSelector(selectApp);
    const [redeemDetail, setRedeemDetail] = useState<any>();
    const [isActive, setActive] = useState<boolean>(false);
    const [isWarning, setIsWarning] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<string>("");

    useEffect(() => {
        // setParam(INITIAL_LIST_PARAMS);
        setCoupons([]);
        getInitCoupon();
    }, [under]);

    const handleCloseDetail = () => {
        setShowPopup(false);
        closeAllPopup();
    };

    const handleCloseWarning = () => {
        setIsWarning(false);
    };

    const getInitCoupon = async () => {
        setSpinning(true);
        await getCoupons();
        setSpinning(false);
    };

    const getCouponAfterRedeem = async () => {
        const res = await couponApi.getListCoupon(param);
        const data = res?.data.docs as ICouponList[];
        setCoupons(data);
    };

    const getCoupons = async () => {
        if (under === enumNavigation.COUPON_MERCHANT) {
            const res = await couponApi.getListCoupon(param);
            const data = res?.data.docs as ICouponList[];
            setCoupons(data);
        } else {
            const res = await couponApi.getListCouponMerchant(param);
            const data = res?.data.docs as ICoupon[];
            setCoupons(data);
        }
    };

    const getMoreCoupons = async () => {
        console.log("getMoreCoupons");
        if (under === enumNavigation.COUPON_MERCHANT) {
            const res = await couponApi.getListCoupon(param);
            const data = res?.data.docs as ICouponList[];
            setCoupons([...(coupons as ICouponList[]), ...data]);
        } else {
            const res = await couponApi.getListCouponMerchant(param);
            const data = res?.data.docs as ICoupon[];
            setCoupons([...(coupons as ICoupon[]), ...data]);
        }
    };

    const handleClickItem = (id: string, alienCode: string, parentId?: string) => {
        dispatch(setCouponCollected(true));
        useCoupon(id, parentId);

        if (under === enumNavigation.COUPON_MERCHANT) {
            // For merchant list items, the parent history id comes from list API.
            if (parentId) {
                fetchPersonalCoppon(parentId);
            } else {
                const index = coupons.findIndex((coupon: any) => coupon?.qrtable?.qrToken === id);
                if (index >= 0 && coupons[index]._id !== undefined) {
                    fetchPersonalCoppon(coupons[index]._id);
                } else {
                    setFocusParentId("");
                }
            }
        }
    };

    const fetchPersonalCoppon = async (idParent: string | any) => {
        try {
            dispatch(setLoading(false));
            if (idParent) {
                const res = await deduplicate(`personal-coupon-${idParent}`, () =>
                    merchantApi.getPersonalCoupon(idParent)
                );
                setCNote(res.data.note);
                setFocusParentId(idParent);
            }
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            }
            history.push("/");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const useCoupon = async (id: string, parentId?: string) => {
        try {
            dispatch(setLoading(true));
            if (id) {
                const res = await deduplicate(`coupon-${id}`, () => merchantApi.getCouponId(id));
                const data = res.data as ICoupon;
                // setCoupon(data);
                const index = configClient.findIndex((item) => item.merchantId === data.merchantId);
                if (index < 0 || userInfo?.status === enumStatus.INACTIVE) {
                    dispatch(setExternalTypes({ type: enumTypes.COUPON, id }));
                } else {
                    dispatch(setExternalTypes(null));
                }
                setACode(res.data.alienCodeTxts);
                setFocusCoupon(res.data);
                if (parentId) {
                    setFocusParentId(parentId);
                }
                setShowPopup(true);
            } else {
                history.push("/");
            }
        } catch (err: any) {
            if (err.response) {
                // error(formatMessage(err.response));
            }
            history.push("/");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleLoadMore = async () => {
        const paramPanigation = {
            ...param,
            page: (param.page || 1) + 1,
        };
        setParam(paramPanigation);
        setLoadingPagination(true);
        await getMoreCoupons();
        setLoadingPagination(false);
    };

    const loadingComponent = (
        <StyledSkeleton>
            <Skeleton
                loading={true}
                active
                title={false}
                paragraph={{ rows: 3, width: [172, 220, 92] }}
            ></Skeleton>
            <Skeleton
                loading={true}
                active
                title={false}
                paragraph={{ rows: 3, width: [172, 220, 92] }}
            ></Skeleton>
            <Skeleton
                loading={true}
                active
                title={false}
                paragraph={{ rows: 3, width: [172, 220, 92] }}
            ></Skeleton>
            <Skeleton
                loading={true}
                active
                title={false}
                paragraph={{ rows: 3, width: [172, 220, 92] }}
            ></Skeleton>
        </StyledSkeleton>
    );

    const [showpage1, setShowPage1] = useState(true);
    const [showpage4, setShowPage4] = useState(false);
    const [showresult, setShowResult] = useState(false);
    const [showpopuppage, setShowPopupPage] = useState(true);

    const closeAllPopup = () => {
        setShowPage1(true);
        setShowPage4(false);
        setShowResult(false);
        setShowPopupPage(true);
    };

    const onFinalRedeem = async () => {
        UIStore.setState({ isLoading: true });
        await null;
        try {
            if (focusParentId) {
                setActive(true);
                let resParent = await couponApi.redeemedCoupon({
                    objectId: focusCoupon?._id,
                    status: enumStatus.ACTIVE,
                    type: enumTypes.COUPON,
                    id: focusParentId,
                });
                let msg = resParent?.data?.message.split("||");
                if (resParent.status === 201) {
                    if (langSetting?.language === "ENGLISH") {
                        success(msg[1] || "");
                    } else {
                        success(msg[0] || "");
                    }
                    setShowPage4(false);
                    setShowResult(true);
                    setShowPopupPage(true);
                    setRedeemDetail(resParent.data?.coupon);
                    setACode(resParent?.data?.coupon?.alienCode);
                    getCoupons();
                    // const paramPanigation = {
                    //     ...param,
                    //     page: (param.page || 1),
                    // };
                    // await getCouponAfterRedeem();
                } else {
                    //
                }
            } else {
                let resNone = await couponApi.redeemedCouponNotId(
                    {
                        objectId: focusCoupon?._id,
                        status: enumStatus.ACTIVE,
                        type: enumTypes.COUPON,
                    },
                    focusCoupon?._id || ""
                );
                let msg = resNone?.data?.message.split("||");
                if (resNone.status === 201) {
                    if (langSetting?.language === "ENGLISH") {
                        success(msg[1] || "");
                    } else {
                        success(msg[0] || "");
                    }
                    setShowPage4(false);
                    setShowResult(true);
                    setShowPopupPage(true);
                    setRedeemDetail(resNone.data?.coupon);
                    setACode(resNone?.data?.coupon?.alienCode);
                } else {
                    //
                }
            }
            dispatch(fetchUser());
            UIStore.setState({ isLoading: false });
        } catch (error: any) {
            setActive(false);
            UIStore.setState({ isLoading: false });
            setWarningMessage(formatMessage(error.response));
            handleCloseDetail();
            setIsWarning(true);
        } finally {
            setActive(false);
            UIStore.setState({ isLoading: false });
        }
    };

    const isDateExpiry = dayjs(focusCoupon?.endDate) < dayjs() ? true : false;

    return (
        <>
            <RenderMeta title={t("meta.coupon_list")} />
            {!spinning ? (
                coupons?.length > 0 ? (
                    <ComponentCouponList
                        handleLoadMore={() => {
                            // handleLoadMore();
                        }}
                        data={coupons}
                        handleClickItem={handleClickItem}
                        loadingPagination={loadingPagination}
                        under={under}
                    />
                ) : (
                    <div style={{ height: "500px" }}>
                        <StyledEmptyLayout>
                            <ModuleEmpty
                                description={t("page.no_coupon")}
                                image={<CouponEmpty />}
                            />
                        </StyledEmptyLayout>
                    </div>
                )
            ) : (
                // <ComponentSpinPage children={loadingComponent} />
                // null
                loadingComponent
            )}

            {isShowPopup && (
                <StyledShowPopup
                    isConfirm={isConfirm}
                    themeColor={themes?.themeColor!}
                    isExpired={isDateExpiry}
                >
                    <div className="page-wrapper">
                        <div className="close" onClick={handleCloseDetail}>
                            <IConClose width={16} />
                        </div>
                        {showpopuppage ? (
                            <div className="content-layout">
                                <div className="content">
                                    {showpage1 ? (
                                        <>
                                            <div className="header-product">
                                                <div className="layout-img">
                                                    <div className="img-product">
                                                        <img src={focusCoupon.imageUrl[0]} />
                                                    </div>
                                                </div>
                                                <div className="topic-product">
                                                    {focusCoupon.name}
                                                </div>
                                                <div className="expier-product">
                                                    {t("page.exp")} :{" "}
                                                    {formatDateStartEnd(
                                                        focusCoupon.startDate,
                                                        focusCoupon.endDate
                                                    )}
                                                </div>
                                            </div>
                                            {focusCoupon?.description ? (
                                                <div className="content-product">
                                                    <div className="topic-detail">
                                                        {t("page.conditions")}
                                                    </div>
                                                    <div
                                                        className="control-text"
                                                        dangerouslySetInnerHTML={{
                                                            __html: focusCoupon?.description,
                                                        }}
                                                    ></div>
                                                </div>
                                            ) : null}
                                            {focusCoupon?.guideline ? (
                                                <div className="content-product">
                                                    <div className="topic-detail">
                                                        {t("page.conditions")}
                                                    </div>
                                                    <div
                                                        className="control-text"
                                                        dangerouslySetInnerHTML={{
                                                            __html: focusCoupon?.guideline,
                                                        }}
                                                    ></div>
                                                </div>
                                            ) : null}
                                            <StyledUsedBtn
                                                themeColor={themes?.themeColor!}
                                                isExpired={isDateExpiry}
                                                onClick={() => {
                                                    !isDateExpiry &&
                                                        (setShowPage1(false),
                                                        setShowPage4(true),
                                                        setShowPopupPage(false));
                                                }}
                                            >
                                                {t("page.redeem")}
                                            </StyledUsedBtn>
                                        </>
                                    ) : null}

                                    {showresult ? (
                                        <>
                                            <div className="set-result">
                                                <div className="layout-img">
                                                    <div className="img-product">
                                                        <img
                                                            src={
                                                                focusCoupon
                                                                    ? focusCoupon.imageUrl[0]
                                                                    : ""
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className="fixed-layout">
                                                    <div className="content-info">
                                                        <div className="header-product">
                                                            <div className="topic-product">
                                                                {focusCoupon.name}
                                                            </div>

                                                            <div className="expier-product">
                                                                {t("page.exp")} :
                                                                {formatDateStartEnd(
                                                                    focusCoupon.startDate,
                                                                    focusCoupon.endDate
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {focusCoupon?.autoUseTime?.enabled && (
                                                        <div className="countdownBox">
                                                            <CountdownTimer
                                                                targetDate={
                                                                    redeemDetail?.autoUseTimeOut
                                                                }
                                                            />
                                                            <div style={{ textAlign: "center" }}>
                                                                The code will expire{" "}
                                                                {focusCoupon?.autoUseTime?.time}{" "}
                                                                {focusCoupon?.autoUseTime
                                                                    ?.timeUnit === "s"
                                                                    ? "seconds "
                                                                    : focusCoupon?.autoUseTime
                                                                          ?.timeUnit === "m"
                                                                    ? "minutes "
                                                                    : focusCoupon?.autoUseTime
                                                                          ?.timeUnit === "h"
                                                                    ? "hours "
                                                                    : " "}
                                                                after clicking the confirm button.
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="action-layout">
                                                        <div className="qr-layout">
                                                            <div className="qr-box">
                                                                <QRCode
                                                                    value={
                                                                        aCode ||
                                                                        redeemDetail?.ticketCode ||
                                                                        ""
                                                                    }
                                                                    size={150}
                                                                />
                                                            </div>
                                                            <StyledCodeButton
                                                                mainColor={themes?.themeColor!}
                                                            >
                                                                {aCode ||
                                                                    redeemDetail?.ticketCode ||
                                                                    "---"}
                                                            </StyledCodeButton>
                                                        </div>
                                                        {aCode !== "" && (
                                                            <StyledAlienCode
                                                                mainColor={themes?.themeColor!}
                                                            >
                                                                <div className="title text-normal-bold">
                                                                    {t("page.couponcode")}
                                                                </div>
                                                                <div className="alien-code-btn">
                                                                    {aCode}
                                                                </div>
                                                                <div className="custom-barcode">
                                                                    <Barcode value={aCode} />
                                                                </div>
                                                            </StyledAlienCode>
                                                        )}
                                                        {focusCoupon.notiStatus === "REDEEMED" ? (
                                                            <p className="redeemed-product">
                                                                <span className="redeemed">
                                                                    <Redeemed color="#336C51" />
                                                                </span>
                                                                {focusCoupon.notiStatus}
                                                            </p>
                                                        ) : null}
                                                        {focusCoupon.notiStatus === "PENDING" ? (
                                                            <p className="pending-product">
                                                                <span className="pending">
                                                                    <Pending color="#ED773A" />
                                                                </span>
                                                                {focusCoupon.notiStatus}
                                                            </p>
                                                        ) : null}
                                                        {focusCoupon.notiStatus === "USED" ? (
                                                            <p className="used-product">
                                                                <span className="used">
                                                                    <Used color="#336C51" />
                                                                </span>
                                                                {focusCoupon.notiStatus}
                                                            </p>
                                                        ) : null}
                                                        <p className="note">
                                                            {focusCoupon?.noticeText
                                                                ? focusCoupon?.noticeText
                                                                : t("page.desConfirm")}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        {showpage4 ? (
                            <StyledPopupRedeem
                                isConfirm={isConfirm}
                                themeColor={themes?.themeColor!}
                            >
                                <div className="box-popupconfirm">
                                    <div className="img-popupconfirm">
                                        <img
                                            src={
                                                merchant?.themes?.basicThemeSetting?.iconGeneral[0]
                                                    ?.image
                                                    ? merchant?.themes?.basicThemeSetting
                                                          ?.iconGeneral[0]?.image
                                                    : focusCoupon?.imageUrl[0]
                                            }
                                            alt=""
                                        />
                                    </div>
                                    <div className="title">
                                        <div className="text-sub-header">
                                            {t("page.pleaseconfirm")}
                                        </div>
                                        {focusCoupon?.autoUseTime?.enabled ? (
                                            <div className="text-desc">
                                                The code will expire in{" "}
                                                {focusCoupon?.autoUseTime?.time}{" "}
                                                {focusCoupon?.autoUseTime?.timeUnit === "s"
                                                    ? "seconds "
                                                    : focusCoupon?.autoUseTime?.timeUnit === "m"
                                                    ? "minutes "
                                                    : focusCoupon?.autoUseTime?.timeUnit === "h"
                                                    ? "hours "
                                                    : " "}
                                                confirmation.
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="btn-popupconfirm">
                                        <StyledUsedBtn
                                            className="btn-cancel"
                                            themeColor={themes?.themeColor!}
                                            onClick={() => {
                                                setShowPage4(false);
                                                setShowPage1(true);
                                                setShowPopupPage(true);
                                            }}
                                        >
                                            {t("page.cancel")}
                                        </StyledUsedBtn>
                                        <StyledUsedBtn
                                            className="btn-redeem"
                                            themeColor={themes?.themeColor!}
                                            // onClick={() => { setShowPage4(false); setShowResult(true); setShowPopupPage(true); }}
                                            onClick={onFinalRedeem}
                                        >
                                            {t("page.confirm")}
                                        </StyledUsedBtn>
                                    </div>
                                </div>
                            </StyledPopupRedeem>
                        ) : null}
                    </div>
                </StyledShowPopup>
            )}
            {isWarning && <PopupWarning onClose={handleCloseWarning} text={warningMessage} />}
        </>
    );
};

const StyledSkeleton = styled.div`
    display: grid;
    padding: 12px;
    .ant-skeleton {
        display: flex;
        background-color: #e8e8e8;
        padding: 12px;
        border-radius: 14px;
        width: 100%;
        margin-bottom: 8px;
        flex-direction: column;
        .ant-skeleton-paragraph {
            margin-top: 0 !important;
            margin-bottom: 45px;
            li {
                height: 10px;
                border-radius: 4px;
                margin-top: 8px;
            }
        }
    }
`;

const StyledPopupRedeem = styled.div<{ isConfirm: boolean; themeColor: string }>`
    height: 100vh;
    width: 100%;
    position: fixed;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 480px;
    background: rgba(0, 0, 0, 0.5);
    .box-popupconfirm {
        background: ${(p) => p.theme.colors.card};
        padding: 65px 28px 28px;
        border-radius: 8px;
        width: calc(100% - 80px);
        max-height: 90%;
        overflow: inherit;
        position: relative;
        ::-webkit-scrollbar {
            width: 4px;
            height: 4px;
        }
        animation: shakebox 0.3s !important;
        @keyframes shakebox {
            0% {
                top: 0px;
                left: 0px;
            }
            25% {
                top: -5px;
                left: -5px;
            }
            50% {
                top: 0;
                left: 0;
            }
            75% {
                top: 5px;
                left: 5px;
            }
            100% {
                top: 0;
                left: 0;
            }
        }
        .img-popupconfirm {
            position: absolute;
            top: -100px;
            left: 50%;
            transform: translateX(-50%);
            img {
                max-width: 150px;
                width: 100%;
            }
        }
        .btn-popupconfirm {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            .btn-redeem {
                color: #fff;
                background-color: ${(p) => p.themeColor};
                width: 100%;
                padding: 10px 0;
                font-size: 13px;
            }
            .btn-cancel {
                color: #000;
                background-color: #fff;
                border: 1px solid #e1e1e1;
                width: 100%;
                padding: 10px 0;
                font-size: 13px;
            }
        }
    }
    .title {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        justify-content: center;
        flex-direction: column;
        .text-sub-header {
            font-size: 18px;
            line-height: 21px;
            color: #000;
            font-weight: 600;
        }
        .text-desc {
            font-size: 11px;
            color: #000;
            font-weight: 400;
        }
    }
`;

const StyledCodeButton = styled.div<{ mainColor?: string }>`
    position: absolute;
    background: ${(p) => p.mainColor};
    border-radius: 80px;
    height: 26px;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    bottom: 0;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    text-align: center;
    color: #ffffff;
`;

const StyledAlienCode = styled.div<{ mainColor?: string }>`
    display: flex;
    align-items: center;
    column-gap: 10px;
    flex-direction: column;
    margin-bottom: 10px;
    .title {
        margin-bottom: 10px;
    }
    .alien-code-btn {
        color: #c4a363;
        margin-bottom: 16px;
        font-size: 20px;
        font-weight: 500;
        margin: 0;
        width: 100%;
        text-align: center;
        padding: 18px 0;
        background-color: #ddd;
        border-radius: 12px;
        background-image: url(../../images/bg-code.svg);
        background-size: cover;
    }
`;

const StyledShowPopup = styled.div<{ isConfirm: boolean; themeColor: string; isExpired?: boolean }>`
    height: 100vh;
    width: 100%;
    position: fixed;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 480px;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10;
    left: 50%;
    transform: translateX(-50%);
    flex-direction: column;
    justify-content: end;
    transition: 2s !important;
    animation: opacity 0.5s !important;
    @keyframes opacity {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    .topic-popup-product {
        width: 100%;
        padding: 16px 24px;
        background: ${(p) => p.theme.colors.card};
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: 8px 8px 0px 0px;
        color: inherit;
        position: relative;
        justify-content: center;
        .close {
            cursor: pointer;
            transform: rotate(0deg);
            transition: 0.6s;
            &:hover {
                transform: rotate(90deg);
            }
        }
    }
    .header {
        width: 100%;
        padding: 16px 24px;
        background: ${(p) => p.theme.colors.card};
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: 8px 8px 0px 0px;
        color: inherit;
        animation: bottomtotop 0.5s !important;
        position: relative;
        @keyframes bottomtotop {
            0% {
                top: 100%;
            }
            80% {
                top: -5%;
            }
            100% {
                top: 0%;
            }
        }
        .close {
            cursor: pointer;
            transform: rotate(0deg);
            transition: 0.6s;
            &:hover {
                transform: rotate(90deg);
            }
        }
    }
    .page-wrapper {
        position: relative;
        width: 100%;
        .close {
            position: absolute;
            left: 18px;
            top: 18px;
            z-index: 1;
        }
    }
    .form-content-product {
        border-radius: 6px;
        margin-bottom: 10px;
        .title {
            font-size: 16px;
            color: #676767;
            margin: 10px 0;
        }
    }
    .ant-select {
        width: 100%;
    }
    .ant-select-selection {
        border-color: #d9d9d9 !important;
        // box-shadow: none !important;
        height: 48px;
        font-size: 16px;
        color: inherit;
        display: flex;
        flex-direction: column;
        justify-content: center;
        background-color: #fff;
        /* margin-bottom: 27px; */
        border-radius: 6px;
    }
    .content-layout-fullpage {
        height: 100vh;
        // background: ${(p) => p.theme.colors.cutter};
        background-color: #f2e8d7;
        // background-image: url(../../images/bg-coupon.svg);
        padding-top: 24px;
        padding-bottom: 24px;
        width: 100%;
        animation: bottomtotop 0.5s !important;
        position: relative;
        @keyframes bottomtotop {
            0% {
                top: 100%;
            }
            80% {
                top: -5%;
            }
            100% {
                top: 0%;
            }
        }
        @media (max-width: ${(p) => p.theme.breakPoints.breakMobileMax}) {
            overflow: scroll;
        }
        .topic-page {
            font-size: 20px;
            text-align: center;
            color: #000;
            font-weight: 600;
        }
    }
    .content-fullpage {
        width: calc(100% - 48px);
        padding-bottom: 28px;
        background: ${(p) => p.theme.colors.card};
        position: relative;
        border-radius: 16px;
        top: 50%;
        transform: translateY(-52%);
        margin: auto;
        padding-bottom: 0;
        .close {
            position: absolute;
            top: 20px;
            right: 20px;
            cursor: pointer;
            transform: rotate(0deg);
            transition: 0.6s;
            &:hover {
                transform: rotate(90deg);
            }
        }
        .content-info {
            border-radius: 16px;
            padding: 20px;
        }
        .btn-redeem {
            color: #fff;
            background-color: ${(p) => (p.isExpired ? "#a9a9a9" : p.themeColor)};
            width: 100%;
            padding: 10px 0;
            font-size: 13px;
        }
        .header-product {
            // border-bottom: 1px solid #F0F0F0;
            padding: 20px;
            .layout-img {
                text-align: center;
                .img-product {
                    width: 141px;
                    height: 141px;
                    border-radius: 100%;
                    overflow: hidden;
                    margin: 0 auto 20px;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
            }
            .topic-product {
                text-align: center;
                font-weight: 600;
                color: #000;
                font-size: 18px;
                line-height: 22px;
                margin-bottom: 5px;
            }
            .total-point-product {
                text-align: center;
                color: #c4a363;
                font-size: 14px;
                margin-bottom: 5px;
            }
            .point-product {
                text-align: center;
                margin-bottom: 5px;
                span {
                    margin-right: 5px;
                }
            }
            .expier-product {
                text-align: center;
                color: #9b9b9b;
            }
        }
        .content-product {
            padding: 16px;
            margin-bottom: 50px;
            text-align: center;
            .topic-detail {
                text-align: center;
                color: ${(p) => p.themeColor};
                font-size: 13px;
                line-height: 16px;
                font-weight: 600;
                margin-bottom: 10px;
            }
            p {
                font-size: 12px;
                line-height: 16px;
                color: #9b9b9b;
                margin-bottom: 16px;
            }
        }
        .interception {
            width: 100%;
            // border-bottom: 5px dotted ${(p) => p.theme.colors.cutter};
            border-bottom: 5px dotted #eaeeff;
            margin-bottom: 5px;
            position: relative;
            display: flex;
            justify-content: space-between;
            .cutter {
                width: 24px;
                height: 24px;
                // background: ${(p) => p.theme.colors.cutter};
                background-color: #f2e8d7;
                border-radius: 12px;
                &.left {
                    position: absolute;
                    left: -14px;
                    bottom: -13px;
                    z-index: 2;
                }
                &.right {
                    position: absolute;
                    right: -14px;
                    bottom: -13px;
                    z-index: 2;
                }
            }
            .middle-cutter {
                height: 9px;
                width: 100%;
                background: #fff;
                position: absolute;
                bottom: -12px;
                right: 0px;
                border-top-left-radius: 13px;
            }
        }
        .action-layout {
            padding: 8px 24px 24px 24px;
            background: #f8eff0;
            border-bottom-left-radius: 24px;
            border-bottom-right-radius: 24px;
            .qr-layout {
                padding: 16px;
                display: flex;
                justify-content: center;
                position: relative;
                margin-bottom: 16px;
                .qr-box {
                    padding: 24px;
                    background: #fff;
                    border-radius: 16px;
                    filter: drop-shadow(0px 3px 12px rgba(143, 143, 143, 0.38));
                }
                .code-btn {
                }
            }
            .note {
                font-style: normal;
                font-weight: 400;
                font-size: 10px;
                line-height: 14px;
                text-align: center;
                color: #000000;
            }
        }
    }
    .content-layout {
        height: auto;
        min-height: 80dvh;
        // background: ${(p) => p.theme.colors.cutter};
        background-color: #fff;
        padding-top: 24px;
        padding-bottom: 24px;
        width: 100%;
        animation: bottomtotop 0.5s !important;
        position: relative;
        border-top-right-radius: 16px;
        border-top-left-radius: 16px;
        @keyframes bottomtotop {
            0% {
                top: 100%;
            }
            80% {
                top: -5%;
            }
            100% {
                top: 0%;
            }
        }
        .content {
            background: transparent !important;
        }
        .btn-redeem {
            color: #fff;
            background-color: ${(p) => p.themeColor};
            width: 100%;
            padding: 10px 0;
            font-size: 13px;
        }
        .set-result {
            .layout-img {
                text-align: center;
                margin-top: -100px;
                .img-product {
                    width: 141px;
                    height: 141px;
                    border-radius: 100%;
                    overflow: hidden;
                    margin: 0 auto 20px;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
            }
            .header-product {
                margin-top: 0;
            }
            .fixed-layout {
                height: 70dvh;
                overflow: scroll;
            }
        }
        .header-product {
            border-bottom: 1px solid #f0f0f0;
            margin-bottom: 20px;
            margin-top: -100px;
            padding-bottom: 20px;
            .layout-img {
                text-align: center;
                .img-product {
                    width: 141px;
                    height: 141px;
                    border-radius: 100%;
                    overflow: hidden;
                    margin: 0 auto 20px;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
            }
            .topic-product {
                text-align: center;
                font-weight: 600;
                color: #000;
                font-size: 18px;
                line-height: 22px;
                margin-bottom: 5px;
            }
            .total-point-product {
                text-align: center;
                color: #c4a363;
                font-size: 14px;
                margin-bottom: 5px;
            }
            .point-product {
                text-align: center;
                margin-bottom: 5px;
                span {
                    margin-right: 5px;
                }
            }
            .expier-product {
                text-align: center;
                color: #9b9b9b;
            }
        }
        .content-product {
            margin-bottom: 50px;
            .topic-detail {
                text-align: center;
                color: ${(p) => p.themeColor};
                font-size: 13px;
                line-height: 16px;
                font-weight: 600;
                margin-bottom: 10px;
            }
            .control-text {
                max-height: 150px;
                overflow: scroll;
                width: 100%;
                word-break: break-all;
            }
            ul {
                li {
                    font-size: 12px;
                    line-height: 16px;
                    color: #9b9b9b;
                }
            }
            p {
                font-size: 12px;
                line-height: 16px;
                color: #9b9b9b;
            }
        }
    }
    .content {
        width: calc(100% - 48px);
        padding-bottom: 28px;
        background: ${(p) => p.theme.colors.card};
        position: relative;
        border-radius: 16px;
        bottom: 0;
        margin: auto;
        padding-bottom: 0;
        .close {
            position: absolute;
            top: 20px;
            right: 20px;
            cursor: pointer;
            transform: rotate(0deg);
            transition: 0.6s;
            &:hover {
                transform: rotate(90deg);
            }
        }
        .content-info {
            padding: 10px;
        }
        .countdownBox {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }
        .interception {
            width: 100%;
            border-bottom: 7px dotted ${(p) => p.theme.colors.cutter};
            margin-bottom: 5px;
            position: relative;
            display: flex;
            justify-content: space-between;
            .cutter {
                width: 24px;
                height: 24px;
                background: ${(p) => p.theme.colors.cutter};
                border-radius: 12px;
                &.left {
                    position: absolute;
                    left: -14px;
                    bottom: -13px;
                    z-index: 2;
                }
                &.right {
                    position: absolute;
                    right: -14px;
                    bottom: -13px;
                    z-index: 2;
                }
            }
            .middle-cutter {
                height: 9px;
                width: 100%;
                background: #f8eff0;
                position: absolute;
                bottom: -12px;
                right: 0px;
                border-top-left-radius: 13px;
            }
        }
        .action-layout {
            padding: 8px 24px 24px 24px;
            // background: #f8eff0;
            border-bottom-left-radius: 24px;
            border-bottom-right-radius: 24px;
            .qr-layout {
                padding: 16px;
                display: flex;
                justify-content: center;
                position: relative;
                margin-bottom: 16px;
                .qr-box {
                    padding: 24px;
                    background: #fff;
                    border-radius: 16px;
                    filter: drop-shadow(0px 3px 12px rgba(143, 143, 143, 0.38));
                }
                .code-btn {
                }
            }
            .code-qr {
                font-weight: 700;
                font-size: 20px;
                line-height: 24px;
                color: #000;
                text-align: center;
            }
            .redeemed-product {
                text-align: center;
                font-size: 12px;
                color: #336c51;
                line-height: 14px;
                span {
                    margin-right: 5px;
                }
            }
            .pending-product {
                text-align: center;
                font-size: 12px;
                color: #ed773a;
                line-height: 14px;
                span {
                    margin-right: 5px;
                }
            }
            .used-product {
                text-align: center;
                font-size: 12px;
                color: #336c51;
                line-height: 14px;
                span {
                    margin-right: 5px;
                }
            }
            .note {
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                line-height: 20px;
                text-align: center;
                color: #9b9b9b;
            }
        }
    }
    .title {
        line-height: 19px;
        color: ${(p) => p.theme.colors.color};
    }
    .desc {
        color: ${(p) => p.theme.colors.color};
        margin-bottom: 16px;
    }
    .desConfirm {
        width: 100%;
        margin-top: 24px;
        text-align: center;
    }
    .confirm-btn-layout {
        margin-top: 100px;
    }
    .btnLayout {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
const StyledUsedBtn = styled.button<{ themeColor: string; isExpired?: boolean }>`
    width: 100%;
    padding: 10px 0;
    font-size: 13px;
    color: ${(p) => (p.isExpired ? "#fff" : p.theme.colors.card)};
    background: ${(p) => (p.isExpired ? "#a9a9a9" : p.themeColor)};
    border: 0.5px solid ${(p) => (p.isExpired ? "#a9a9a9" : p.themeColor)};
    border-radius: 40px;
    cursor: pointer;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    &:active {
        background: ${(p) => p.themeColor};
        color: ${(p) => p.theme.colors.color};
    }
`;

const StyledEmptyLayout = styled.div`
    .ant-empty-description {
        font-weight: 400;
        font-size: 16px;
        line-height: 22px;
        text-align: center;
        color: #a5a5a5;
    }
`;
const StyledLoading = styled.div`
    margin: 25px 18px 0 18px;
    .card {
        box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.16), 1px 3px 6px rgba(0, 0, 0, 0.23);
        margin-bottom: 24px;
        background-color: ${(p) => p.theme.colors.card};
        border-radius: 6px;
        padding: 24px;
        .title {
            margin-bottom: 10px;
        }
    }
`;
